/**
 * Google Calendar Query Hooks
 *
 * React Query hooks for Google Calendar operations following NotePlan Web conventions.
 * These hooks handle all data fetching and mutations for Google Calendar functionality.
 */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useAuthenticatedUser } from '../../providers/UserProvider'
import { useGapiIsReady } from '../Gapi'
import {
  googleQueryKeys,
  transformGoogleEventsToCalendarEvents,
  getCalendars,
  getEvents,
} from '../../lib/google'
import { CalendarSource, ExternalCalendar } from '../../utils/calendar'
import type { CalendarEvent } from '../../modules/calendar/CalendarEvent'
import {
  getAccounts,
  addAccount,
  removeAccount,
  updateAccountStatus,
} from '../../lib/google/accounts'
import { getAccessToken, signOut } from '../../lib/google/auth'
import type {
  GoogleAccount,
  GoogleAccountStatus,
  AccessTokenResponse,
  AuthenticationRequest,
  AuthenticationResponse,
  GoogleAccountOperationResponse,
} from '../../lib/google/types'
import { DEFAULT_CALENDAR_COLOR } from '../../utils/color'
import { googleCalendarDataContext } from './persister'

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if a Google account has placeholder migration data
 * @param account Google account to check
 * @returns True if account needs user info update
 */
function isMigratedAccount(account: GoogleAccount): boolean {
  return (
    (account.email.startsWith('pending_') && account.email.endsWith('@migration.local')) ||
    account.google_user_id.startsWith('pending_')
  )
}

/**
 * Get status color for UI display
 */
function getStatusColor(status: GoogleAccountStatus): string {
  switch (status) {
    case 'active': {
      return 'text-green-800 dark:text-green-600'
    }
    case 'error': {
      return 'text-red-400 dark:text-red-300'
    }
    case 'expired': {
      return 'text-yellow-600 dark:text-yellow-400'
    }
    default: {
      return 'text-gray-600 dark:text-gray-400'
    }
  }
}

/**
 * Get status label for UI display
 */
function getStatusLabel(status: GoogleAccountStatus): string {
  switch (status) {
    case 'active': {
      return 'Active'
    }
    case 'error': {
      return 'Error'
    }
    case 'expired': {
      return 'Expired'
    }
    default: {
      return 'Unknown'
    }
  }
}

// ============================================================================
// Query Option Templates
// ============================================================================

/**
 * Query options template for Google account queries
 * Accounts don't change frequently, so we use longer cache times
 */
const accountsQueryOptions = {
  context: googleCalendarDataContext,
  staleTime: 2 * 60 * 1000, // 2 minutes - accounts don't change frequently
  cacheTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
  refetchInterval: 10 * 60 * 1000, // 10 minutes - less frequent polling
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
  retry: 1,
} as const

/**
 * Query options template for access token queries
 * Tokens need frequent refresh to avoid expiration
 */
const accessTokenQueryOptions = {
  context: googleCalendarDataContext,
  refetchInterval: 55 * 60 * 1000, // 55 minutes
  refetchIntervalInBackground: true,
  refetchOnWindowFocus: false,
  refetchOnReconnect: true,
  retry: 1,
} as const

/**
 * Query options template for calendar queries
 * Calendars change infrequently, so we use moderate cache times
 */
const calendarsQueryOptions = {
  context: googleCalendarDataContext,
  staleTime: 5 * 60 * 1000, // 5 minutes - calendars change infrequently
  cacheTime: 15 * 60 * 1000, // 15 minutes - keep calendars cached longer
  refetchInterval: 15 * 60 * 1000, // 15 minutes - less frequent polling
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: false, // Don't refetch calendars on focus
  refetchOnReconnect: true,
  retry: 1,
} as const

/**
 * Query options template for event queries
 * Events change more frequently, so we use shorter cache times
 */
const eventsQueryOptions = {
  context: googleCalendarDataContext,
  staleTime: 1 * 60 * 1000, // 1 minute - events change more frequently
  cacheTime: 5 * 60 * 1000, // 5 minutes - shorter cache for events
  refetchInterval: 5 * 60 * 1000, // 5 minutes - keep current interval for events
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true, // Keep focus refetch for events
  refetchOnReconnect: true,
  retry: 1,
} as const

// ============================================================================
// Memoized Select Functions
// ============================================================================

/**
 * Stable select function to check if user has any Google accounts
 * We use a stable function reference to memoize the result and avoid unnecessary re-runs on every render
 * @see https://tkdodo.eu/blog/react-query-data-transformations#3-using-the-select-option
 */
function selectHasAccounts(accounts: GoogleAccount[]): boolean {
  return accounts.length > 0
}

/**
 * Stable select function to get active Google accounts
 */
function selectActiveAccounts(accounts: GoogleAccount[]): GoogleAccount[] {
  return accounts.filter((account) => account.status === 'active')
}

/**
 * Stable select function factory to get display information for a specific account
 */
function createSelectAccountDisplay(accountId: string) {
  return (accounts: GoogleAccount[]) => {
    const account = accounts.find((account) => account.account_id === accountId)
    if (!account) return

    return {
      id: account.account_id,
      email: account.email,
      name: account.name || account.email.split('@')[0],
      displayName: account.name || account.email.split('@')[0],
      status: account.status,
      statusColor: getStatusColor(account.status),
      statusLabel: getStatusLabel(account.status),
      isActive: account.status === 'active',
      hasError: account.status === 'error',
      isExpired: account.status === 'expired',
    }
  }
}

/**
 * Stable select function to get account status information
 */
function selectAccountStatus(accounts: GoogleAccount[]) {
  const hasErrors = accounts.some((account) => account.status === 'error')
  const hasExpired = accounts.some((account) => account.status === 'expired')
  const errorAccounts = accounts.filter((account) => account.status === 'error')
  const expiredAccounts = accounts.filter(
    (account) => account.status === 'expired'
  )
  const activeAccounts = accounts.filter(
    (account) => account.status === 'active'
  )

  return {
    hasErrors,
    hasExpired,
    errorAccounts,
    expiredAccounts,
    total: accounts.length,
    active: activeAccounts.length,
    error: errorAccounts.length,
    expired: expiredAccounts.length,
  }
}

// ============================================================================
// Account Query Hooks
// ============================================================================

// Overload signatures for useGoogleAccountsQuery factory
function useGoogleAccountsQuery(): UseQueryResult<GoogleAccount[], AxiosError>
function useGoogleAccountsQuery<TData>(
  select: (data: GoogleAccount[]) => TData
): UseQueryResult<TData, AxiosError>

// Implementation
function useGoogleAccountsQuery<TData = GoogleAccount[]>(
  select?: (data: GoogleAccount[]) => TData
): UseQueryResult<TData | GoogleAccount[], AxiosError> {
  const user = useAuthenticatedUser()
  const queryClient = useQueryClient({ context: googleCalendarDataContext })

  const query = useQuery({
    ...accountsQueryOptions,
    queryKey: googleQueryKeys.accounts(user.userId),
    queryFn: () => getAccounts(user.userId),
    select,
  })

  // Trigger token refresh for migrated accounts to update user info
  useEffect(() => {
    if (query.data && !query.isLoading && !query.isError) {
      const accounts = Array.isArray(query.data)
        ? (query.data as GoogleAccount[])
        : []

      // Find accounts that need user info updates
      const migratedAccounts = accounts.filter((account) =>
        isMigratedAccount(account)
      )

      if (migratedAccounts.length > 0) {
        // Trigger token refresh for each migrated account
        // This will cause the backend to fetch and update user info
        for (const account of migratedAccounts) {
          // Prefetch access token which triggers refresh and user info update
          queryClient
            .prefetchQuery({
              queryKey: googleQueryKeys.accessToken(
                user.userId,
                account.account_id
              ),
              queryFn: () => getAccessToken(user.userId, account.account_id),
              staleTime: 0, // Force refresh
            })
            .then(() => {
              // After successful token refresh, invalidate accounts to get updated user info
              setTimeout(() => {
                void queryClient.invalidateQueries(
                  googleQueryKeys.accounts(user.userId)
                )
              }, 1000) // Small delay to allow backend processing
            })
            .catch((error: unknown) => {
              // Log error but don't fail the accounts query
              console.warn(
                `Failed to refresh token for migrated account ${account.account_id}:`,
                error
              )
            })
        }
      }
    }
  }, [query.data, query.isLoading, query.isError, user.userId, queryClient])

  return query as UseQueryResult<TData | GoogleAccount[], AxiosError>
}

/**
 * Hook to get all Google accounts for the current user (React Query)
 * This is the main factory function that can optionally accept a select function
 * for data transformation, similar to useNotesFactory pattern
 */
export { useGoogleAccountsQuery }

/**
 * Hook to check if user has any Google accounts using React Query select option
 */
export function useHasGoogleAccounts() {
  return useGoogleAccountsQuery(selectHasAccounts)
}

/**
 * Hook to get active Google accounts using React Query select option
 */
export function useActiveGoogleAccounts() {
  return useGoogleAccountsQuery(selectActiveAccounts)
}

/**
 * Hook to get display information for a specific account using React Query select option
 */
export function useGoogleAccountDisplay(accountId: string) {
  return useGoogleAccountsQuery(createSelectAccountDisplay(accountId))
}

/**
 * Hook to get account status information using React Query select option
 */
export function useGoogleAccountStatus() {
  return useGoogleAccountsQuery(selectAccountStatus)
}

/**
 * Hook to add a new Google account
 */
export function useAddGoogleAccount() {
  const user = useAuthenticatedUser()
  const queryClient = useQueryClient({ context: googleCalendarDataContext })

  return useMutation<AuthenticationResponse, AxiosError, string>({
    context: googleCalendarDataContext,
    mutationFn: (code) => {
      const request: AuthenticationRequest = {
        code,
        userid: user.userId,
      }
      return addAccount(request)
    },
    onSuccess: (data) => {
      // Invalidate accounts list to refresh
      void queryClient.invalidateQueries(googleQueryKeys.accounts(user.userId))

      // Set the new account's access token in cache
      queryClient.setQueryData(
        googleQueryKeys.accessToken(user.userId, data.account_id),
        {
          token: data.token,
          expires: data.expires,
          account_id: data.account_id,
        }
      )
    },
  })
}

/**
 * Hook to remove a Google account
 */
export function useRemoveGoogleAccount() {
  const user = useAuthenticatedUser()
  const queryClient = useQueryClient({ context: googleCalendarDataContext })

  return useMutation<GoogleAccountOperationResponse, AxiosError, string>({
    context: googleCalendarDataContext,
    mutationFn: (accountId) => removeAccount(user.userId, accountId),
    onSuccess: (_, accountId) => {
      // Remove all cached data for this account
      queryClient.removeQueries(
        googleQueryKeys.accessToken(user.userId, accountId)
      )
      queryClient.removeQueries(
        googleQueryKeys.calendars(user.userId, accountId)
      )

      // Refresh accounts list
      void queryClient.invalidateQueries(googleQueryKeys.accounts(user.userId))
    },
  })
}

/**
 * Hook to update Google account status
 */
export function useUpdateGoogleAccountStatus() {
  const user = useAuthenticatedUser()
  const queryClient = useQueryClient({ context: googleCalendarDataContext })

  return useMutation<
    GoogleAccountOperationResponse,
    AxiosError,
    { accountId: string; status: GoogleAccountStatus }
  >({
    context: googleCalendarDataContext,
    mutationFn: ({ accountId, status }) =>
      updateAccountStatus(user.userId, accountId, status),
    onSuccess: () => {
      // Refresh accounts list to update status
      void queryClient.invalidateQueries(googleQueryKeys.accounts(user.userId))
    },
  })
}

// ============================================================================
// Authentication Query Hooks
// ============================================================================

/**
 * Hook to get access token for a specific Google account
 */
export function useAccessTokenForAccount(accountId: string) {
  const user = useAuthenticatedUser()

  return useQuery<AccessTokenResponse, AxiosError>({
    ...accessTokenQueryOptions,
    queryKey: googleQueryKeys.accessToken(user.userId, accountId),
    queryFn: () => getAccessToken(user.userId, accountId),
  })
}

// ============================================================================
// Reusable Query Functions
// ============================================================================

/**
 * Reusable query function for fetching calendars for a specific account
 * Used by useGoogleCalendarsForAccount and useAllGoogleCalendarEvents
 */
async function fetchCalendarsForAccount(
  userId: string,
  accountId: string,
  queryClient: ReturnType<typeof useQueryClient>
): Promise<ExternalCalendar[]> {
  try {
    const calendars = await getCalendars(userId, accountId)

    return calendars.map((calendar: gapi.client.calendar.CalendarListEntry) => {
      const calendarName = calendar.summary || 'Untitled Calendar'

      return {
        id: calendar.id,
        name: calendarName,
        color: calendar.backgroundColor ?? DEFAULT_CALENDAR_COLOR,
        source: CalendarSource.Google,
      } as ExternalCalendar
    })
  } catch (error) {
    const googleError = error as gapi.client.HttpRequestRejected
    if (googleError.status === 401 || googleError.status === 403) {
      // remove the expired access token and try again with a new one
      queryClient.removeQueries(googleQueryKeys.accessToken(userId, accountId))
    }
    throw error
  }
}

/**
 * Reusable query function for fetching events for a specific calendar
 * Used by useGoogleCalendarEventsForAccount and useAllGoogleCalendarEvents
 */
async function fetchEventsForCalendar(
  userId: string,
  accountId: string,
  calendarId: string,
  dateKey: string
): Promise<CalendarEvent[]> {
  const events = await getEvents(userId, accountId, calendarId, dateKey)
  const selectedDay = dayjs(dateKey, 'YYYYMMDD')
  return transformGoogleEventsToCalendarEvents(events, selectedDay)
}

// ============================================================================
// Calendar Query Hooks
// ============================================================================

/**
 * Hook to get calendars for a specific Google account
 */
export function useGoogleCalendarsForAccount(accountId: string) {
  const user = useAuthenticatedUser()
  const gapiIsReady = useGapiIsReady()
  const queryClient = useQueryClient({ context: googleCalendarDataContext })

  return useQuery<ExternalCalendar[], AxiosError>({
    ...calendarsQueryOptions,
    queryKey: googleQueryKeys.calendars(user.userId, accountId),
    queryFn: () =>
      fetchCalendarsForAccount(user.userId, accountId, queryClient),
    enabled: gapiIsReady,
  })
}

// ============================================================================
// Event Query Hooks
// ============================================================================

/**
 * Hook to get calendar events for a specific account and day
 */
export function useGoogleCalendarEventsForAccount(
  accountId: string,
  selectedDay: dayjs.Dayjs
) {
  const user = useAuthenticatedUser()
  const key = selectedDay.format('YYYYMMDD')
  const gapiIsReady = useGapiIsReady()
  const { data: googleCalendars = [] } = useGoogleCalendarsForAccount(accountId)

  return useQueries({
    context: googleCalendarDataContext,
    queries: googleCalendars.map((calendar: ExternalCalendar) => ({
      ...eventsQueryOptions,
      enabled: gapiIsReady,
      queryKey: googleQueryKeys.events(
        user.userId,
        accountId,
        calendar.id,
        key
      ),
      queryFn: () =>
        fetchEventsForCalendar(user.userId, accountId, calendar.id, key),
    })),
  })
}

/**
 * Hook to get calendar events for all accounts on a specific day
 */
export function useAllGoogleCalendarEvents(selectedDay: dayjs.Dayjs) {
  const { data: activeAccounts = [] } = useActiveGoogleAccounts()
  const user = useAuthenticatedUser()
  const gapiIsReady = useGapiIsReady()
  const key = selectedDay.format('YYYYMMDD')

  // Get calendars for all active accounts using the extracted reusable function
  const queryClient = useQueryClient({ context: googleCalendarDataContext })
  const calendarQueries = useQueries({
    context: googleCalendarDataContext,
    queries: activeAccounts.map((account) => ({
      ...calendarsQueryOptions,
      queryKey: googleQueryKeys.calendars(user.userId, account.account_id),
      queryFn: () =>
        fetchCalendarsForAccount(user.userId, account.account_id, queryClient),
      enabled: gapiIsReady,
    })),
  })

  // Create event queries for all calendars across all accounts using the extracted reusable function
  const allCalendarEventQueries = activeAccounts.flatMap(
    (account, accountIndex) => {
      const calendars = calendarQueries[accountIndex]?.data ?? []

      return calendars.map((calendar: ExternalCalendar) => ({
        ...eventsQueryOptions,
        enabled: gapiIsReady,
        queryKey: googleQueryKeys.events(
          user.userId,
          account.account_id,
          calendar.id,
          key
        ),
        queryFn: () =>
          fetchEventsForCalendar(
            user.userId,
            account.account_id,
            calendar.id,
            key
          ),
      }))
    }
  )

  // Get events for all calendars using the same query pattern as useGoogleCalendarEventsForAccount
  const eventQueries = useQueries({
    context: googleCalendarDataContext,
    queries: allCalendarEventQueries,
  })

  // Flatten all event results
  const allEvents = eventQueries.flatMap((result) => result.data ?? [])
  const isLoading =
    calendarQueries.some((result) => result.isLoading) ||
    eventQueries.some((result) => result.isLoading)
  const isRefetching =
    calendarQueries.some((result) => result.isRefetching) ||
    eventQueries.some((result) => result.isRefetching)
  const isError =
    calendarQueries.some((result) => result.isError) ||
    eventQueries.some((result) => result.isError)

  return {
    data: allEvents,
    isLoading,
    isRefetching,
    isError,
  }
}

// ============================================================================
// Sign Out Functions
// ============================================================================

/**
 * Hook for signing out all Google accounts for a user (used by UserProvider)
 */
export function useSignOut() {
  const queryClient = useQueryClient({ context: googleCalendarDataContext })

  return useMutation({
    context: googleCalendarDataContext,
    mutationFn: signOut,
    onSuccess: () => {
      queryClient.clear()
    },
  })
}
